import { useState } from "react";
import { Shield, Target, User, Play, Download, X } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";
import { Button } from "../../components/ui/button";

export default function PublicStandings() {
  const { data, loading } = usePublicData();
  const [showVideo, setShowVideo] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { standings, player_stats } = data;

  const topScorers = [...player_stats]
    .filter((p) => p.goals > 0)
    .sort((a, b) => b.goals - a.goals)
    .slice(0, 10);

  const topAssists = [...player_stats]
    .filter((p) => p.assists > 0)
    .sort((a, b) => b.assists - a.assists)
    .slice(0, 10);

  const videoUrl = "/standings_race.webm";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Standings
        </h1>
        <p className="text-muted-foreground mt-1">
          League table and top performers
        </p>
      </div>

      {/* Season Timeline Video Banner */}
      <div className="bg-gradient-to-r from-primary/20 via-amber-500/20 to-primary/20 border border-primary/30 rounded-xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase flex items-center justify-center md:justify-start gap-2">
              <Play className="w-5 h-5 text-primary" />
              Season Timeline
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Watch how the standings evolved from Day 1 to Championship Day!
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowVideo(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="watch-timeline-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Watch Video
            </Button>
            <Button
              variant="outline"
              asChild
              data-testid="download-timeline-btn"
            >
              <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl bg-card rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold uppercase">Season Standings Timeline</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowVideo(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <video
                src={videoUrl}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: "70vh" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="p-4 border-t border-border flex justify-end">
              <Button variant="outline" asChild>
                <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Standings Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 text-left">#</th>
                <th className="p-3 text-left">Team</th>
                <th className="p-3 text-center">P</th>
                <th className="p-3 text-center">W</th>
                <th className="p-3 text-center">D</th>
                <th className="p-3 text-center">L</th>
                <th className="p-3 text-center">GF</th>
                <th className="p-3 text-center">GA</th>
                <th className="p-3 text-center">GD</th>
                <th className="p-3 text-center">PTS</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, index) => (
                <tr
                  key={team.team_id}
                  className={`border-t border-border ${
                    index < 4 ? "border-l-2 border-l-primary" : ""
                  }`}
                >
                  <td className="p-3">
                    <span
                      className={`w-6 h-6 rounded flex items-center justify-center text-sm font-bold ${
                        index === 0
                          ? "bg-amber-500/20 text-amber-500"
                          : index < 4
                          ? "bg-primary/20 text-primary"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {index + 1}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {team.team_logo ? (
                        <img src={team.team_logo} alt="" className="w-8 h-8 rounded object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                          <Shield className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <span className="font-medium">{team.team_name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-center text-muted-foreground">{team.played}</td>
                  <td className="p-3 text-center text-green-500 font-medium">{team.won}</td>
                  <td className="p-3 text-center text-muted-foreground">{team.drawn}</td>
                  <td className="p-3 text-center text-red-500">{team.lost}</td>
                  <td className="p-3 text-center">{team.goals_for}</td>
                  <td className="p-3 text-center">{team.goals_against}</td>
                  <td className={`p-3 text-center font-medium ${
                    team.goal_difference > 0 ? "text-green-500" : 
                    team.goal_difference < 0 ? "text-red-500" : ""
                  }`}>
                    {team.goal_difference > 0 ? "+" : ""}{team.goal_difference}
                  </td>
                  <td className="p-3 text-center">
                    <span className="font-heading text-xl font-bold text-primary">
                      {team.points}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-border text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 bg-primary rounded" /> Knockout Qualification
          </span>
          <span className="ml-4">Win: 3pts | Draw: 1pt | Loss: 0pts</span>
        </div>
      </div>

      {/* Top Performers */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Top Scorers
          </h3>
          {topScorers.length > 0 ? (
            <div className="space-y-2">
              {topScorers.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                  <span className="w-6 text-center font-bold text-muted-foreground">{i + 1}</span>
                  <span className="flex-1">{p.player_name}</span>
                  <span className="text-xs text-muted-foreground">{p.team_name}</span>
                  <span className="font-heading text-xl font-bold text-primary">{p.goals}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No goals yet</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent" /> Top Assists
          </h3>
          {topAssists.length > 0 ? (
            <div className="space-y-2">
              {topAssists.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50">
                  <span className="w-6 text-center font-bold text-muted-foreground">{i + 1}</span>
                  <span className="flex-1">{p.player_name}</span>
                  <span className="text-xs text-muted-foreground">{p.team_name}</span>
                  <span className="font-heading text-xl font-bold text-accent">{p.assists}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No assists yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
